import { useTranslation } from 'react-i18next';

import { DIFF_STATUS_CFG } from '~/constants/diff';
import type { AttributeDiffRow, AttributeDiffSide } from '~/types/product-diff';
import { cn } from '~/utils/cn';

/** Which side of the diff a cell renders. */
type Side = 'before' | 'after';

/** Statuses whose current-side value no longer describes the product. The badge
 *  text carries the same fact for screen readers and anyone who can't see the
 *  line — strikethrough is never the only signal. */
const STRUCK_STATUSES = new Set(['removed', 'replaced', 'edited']);

/**
 * Values for one side. A single value renders as plain text and multiple values
 * as chips: chipping a lone value decorates the common case, and a plain
 * comma-joined list of eight makes it impossible to see where one value ends.
 */
function AttributeValues({
  values,
  struck,
  testId,
}: {
  values: string[];
  struck: boolean;
  /** `data-testid` for the value; multi-value chips derive `-item-{i}`. */
  testId: string;
}) {
  if (values.length === 1) {
    return (
      <span
        className={cn('text-sm', struck ? 'text-muted-foreground line-through' : '')}
        data-testid={testId}
      >
        {values[0]}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap justify-start gap-1.5 md:justify-end" data-testid={testId}>
      {values.map((value, i) => (
        <span
          key={`${value}-${String(i)}`}
          data-testid={`${testId}-item-${String(i)}`}
          className={cn(
            'rounded-sm px-2 py-0.5 text-[13px] leading-5 ring-1 ring-inset',
            struck
              ? 'bg-neutral-10 text-muted-foreground ring-neutral-40 line-through'
              : 'bg-neutral-20 text-foreground ring-neutral-40',
          )}
        >
          {value}
        </span>
      ))}
    </span>
  );
}

/**
 * Status pill next to the attribute name. Colour and label both come from the
 * shared status map, so a retune in one place moves every surface.
 *
 * `quiet` statuses render the label for screen readers only. On a typical product
 * nearly every row is an addition, and a green "Added" chip on all of them — next
 * to a "Not present" cell already saying so — is noise that crowds out the two or
 * three rows a reviewer actually has to judge.
 */
function StatusBadge({
  status,
  testId,
}: {
  status: AttributeDiffRow['status'];
  /** `data-testid` for the badge (also set on the screen-reader-only variant). */
  testId: string;
}) {
  const { t } = useTranslation('common');
  const cfg = DIFF_STATUS_CFG[status];
  const label = t(`productDetailsComparison.status.${cfg.labelKey}`);

  if (cfg.quiet)
    return (
      <span className="sr-only" data-testid={testId}>
        {label}
      </span>
    );
  return (
    <span
      className={cn(
        'shrink-0 rounded-sm px-1.5 py-0.5 text-[11px] leading-4 font-semibold',
        cfg.badge,
      )}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

/**
 * One cell of a diff row: attribute name (plus status badge on the optimized
 * side) on the left, values on the right.
 *
 * An absent side gets words rather than a dash on the current side: "never
 * existed" and "was deleted" are different facts, and a reviewer acts on them
 * differently.
 */
function DiffCell({
  row,
  side,
  testId,
}: {
  row: AttributeDiffRow;
  side: Side;
  /** `data-testid` root of the pane; derives `-empty`, `-name`, `-status-badge`, `-value`, `-note`. */
  testId: string;
}) {
  const { t } = useTranslation('common');
  const data: AttributeDiffSide | undefined = side === 'before' ? row.before : row.after;

  if (!data) {
    return (
      <div className="flex min-h-13 items-center px-4 py-3">
        {/* Lighter than a value and lighter than an attribute name: on an
            addition-heavy product this string repeats down the whole column, so it
            has to read as a placeholder rather than as content. */}
        <span className="text-muted-foreground/70 text-xs" data-testid={`${testId}-empty`}>
          {side === 'before'
            ? t('productDetailsComparison.notPresent')
            : t('productDetailsComparison.noOptimizedValue')}
        </span>
      </div>
    );
  }

  const struck = side === 'before' && STRUCK_STATUSES.has(row.status);
  // The badge sits on the optimized side, except for a removed row where there
  // is no optimized cell to carry it.
  const showBadge = side === 'after' || row.status === 'removed';
  // A note explains the optimized side's choice, so it renders there — unless
  // the row has no optimized side at all.
  const showNote = row.note !== undefined && (side === 'after' ? true : !row.after);

  return (
    <div className="flex min-h-13 flex-col gap-1.5 px-4 py-3 md:flex-row md:items-start md:justify-between md:gap-6">
      <span className="flex shrink-0 flex-wrap items-center gap-2">
        <span
          className={cn(
            'text-sm font-medium',
            struck ? 'text-muted-foreground line-through' : 'text-foreground',
          )}
          data-testid={`${testId}-name`}
        >
          {data.name}
        </span>
        {showBadge && <StatusBadge status={row.status} testId={`${testId}-status-badge`} />}
      </span>
      <span className="min-w-0 md:text-right">
        <AttributeValues values={data.values} struck={struck} testId={`${testId}-value`} />
        {showNote && (
          <span
            className="text-muted-foreground mt-1 block text-[11.5px]"
            data-testid={`${testId}-note`}
          >
            {row.note}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * A full-width diff row for the `aligned` layout: both cells as grid siblings so
 * they always share a height. Only the side that carries the change is washed
 * (see `DiffPane`), so the opposite "Current"/"Lily" column stays neutral. Below
 * `md` they stack, each labelled, since a two-column diff at phone width is
 * unreadable.
 */
export function AttributeDiffRowAligned({
  row,
  testId = 'attribute-diff-row',
}: {
  row: AttributeDiffRow;
  /** `data-testid` for the row; its panes derive `-current` / `-enriched`. */
  testId?: string;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2" data-testid={testId}>
      <DiffPane
        row={row}
        side="before"
        className="border-b md:border-r md:border-b-0"
        testId={`${testId}-current`}
      />
      <DiffPane row={row} side="after" testId={`${testId}-enriched`} />
    </div>
  );
}

/**
 * One side of a row, wrapped with its status wash. Exported for the `panels`
 * layout, which renders each side in its own scroll container and therefore needs
 * the halves separately.
 */
export function DiffPane({
  row,
  side,
  className,
  testId = side === 'before' ? 'attribute-diff-row-current' : 'attribute-diff-row-enriched',
}: {
  row: AttributeDiffRow;
  side: Side;
  className?: string;
  /** `data-testid` for the pane (carries `data-diff-status`); derives the cell ids. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const cfg = DIFF_STATUS_CFG[row.status];

  // The wash marks only the side that carries the change — the enriched value for
  // an addition/edit/replacement, the current value for a removal — so the
  // opposite column reads as neutral instead of being painted as if it changed.
  const washSide: Side = row.status === 'removed' ? 'before' : 'after';
  const rowBg = side === washSide ? cfg.rowBg : '';

  // No status bar on either edge: the row wash and the badge already carry the
  // status, and a 3px colour block on each cell read as a heavy left border plus a
  // second one masquerading as the column divider. The divider between the two
  // sides is the hairline border the caller passes in.
  return (
    <div
      className={cn('min-w-0', rowBg, className)}
      data-testid={testId}
      data-diff-status={row.status}
    >
      {/* Stacked-layout side label: with the columns gone, nothing else says
          which of the two values a reviewer is looking at. */}
      <span
        className={cn(
          'block px-4 pt-2.5 text-[10.5px] font-medium tracking-[0.12em] uppercase md:hidden',
          side === 'before' ? 'text-muted-foreground' : 'text-primary',
        )}
      >
        {side === 'before'
          ? t('productDetailsComparison.currentShort')
          : t('productDetailsComparison.lilyShort')}
      </span>
      <DiffCell row={row} side={side} testId={testId} />
    </div>
  );
}
